import * as Sentry from '@sentry/react';
import { reportError } from './reportError';
import { expectedErrorCategory, type ExpectedErrorCategory } from './isExpectedError';

// The SSE hooks' `onopen` throws `new Error('SSE connection failed with status <n>')` on a non-2xx response.
const SSE_STATUS = /SSE connection failed with status (\d+)/;

const sseErrorCategory = (error: unknown): ExpectedErrorCategory | null => {
  const status = error instanceof Error ? SSE_STATUS.exec(error.message)?.[1] : undefined;
  if (status) return status === '401' || status === '403' ? 'auth' : null;
  return expectedErrorCategory(error);
};

/**
 * Sentry reporting for a fetchEventSource `onerror`. Create one per stream connection.
 * - Expected failures (dropped connection, offline, expired session) → breadcrumb only.
 * - Anything else (5xx, bad payload) → reported once; the streams that keep retrying
 *   (every ~1 s) would otherwise send the same failure on every attempt.
 */
export const createSseErrorReporter = (tags: Record<string, string>) => {
  let reported = false;

  return (error: unknown) => {
    const category = sseErrorCategory(error);
    if (category) {
      Sentry.addBreadcrumb({
        category,
        level: 'info',
        message: `SSE ${category} error`,
        data: { ...tags, error: String(error) },
      });
      return;
    }
    if (reported) return;
    reported = true;
    reportError(error, { tags });
  };
};
